import { apiService } from '@shared/services/apiServices';
import { AxiosError } from 'axios';
import contactRequestEndpoints from './contactRequestEndpoints';
import {
  handleContactRequestError,
  ContactRequestErrorCode,
  contactRequestErrorMessages,
} from '../errors';

// ─── Response shape ───────────────────────────────────────────────────────────

/** Single admin note entry returned inside the admin_notes array. */
export interface AdminNote {
  note: string;
  /** ISO-8601 string — format before display */
  created_at: string;
}

/**
 * A single completion "response" attachment. Only `url` + `filename` are
 * returned by the view endpoints — no file size (see [[rr915-response-tab]]).
 */
export interface ContactRequestResponseAttachment {
  url: string | null;
  filename: string;
}

/**
 * Completion "response" payload — the assigned admin's uploaded documents and
 * free-text note. Populated only once the request is `completed`; a future
 * feature on the backend (RR-1096 does not yet return it), so treat an absent
 * object, `null` fields, and empty arrays all as "no response yet".
 */
export interface ContactRequestResponse {
  attachments: ContactRequestResponseAttachment[];
  notes: string | null;
}

/**
 * A single enriched entry in the contact-request status-history timeline
 * (RR-1096). `previous_status === null` marks the "created" event.
 * `performed_by_name` is `null` when the actor is no longer a resolvable
 * user/client. `performed_by_role` is the role captured at the time of action.
 */
export interface ContactRequestHistoryEntry {
  id: string;
  previous_status: string | null;
  new_status: string;
  /** Actor's Cognito sub — an opaque id, not for display. */
  performed_by_id: string | null;
  performed_by_name: string | null;
  performed_by_role: string;
  /** ISO-8601 string — format before display */
  updated_at: string;
}

/** Full detail payload returned by GET contact-request/contact-requests/:id */
export interface ViewContactRequestData {
  /** Internal UUID — used as the route param */
  id: string;
  /** Human-readable sequential ID, e.g. SR2026000010 */
  contact_request_id: string;
  subject: string;
  /** Body of the contact request (max 500 chars) */
  message: string;
  /**
   * Admin-only notes array. No longer returned by the view endpoints (the
   * backend dropped it from the response contract), so treat as optional.
   */
  admin_notes?: AdminNote[];
  status: string;
  /** UUID of the assigned admin (users.id), or null when unassigned. */
  assigned_to: string | null;
  /**
   * Also a UUID (duplicates `assigned_to`), despite the name — NOT the
   * human-readable sequential id. Use `assigned_user_sequential_id` for display.
   */
  assigned_user_id: string | null;
  /** Human-readable sequential id of the assigned admin, e.g. ADMIN00000001. */
  assigned_user_sequential_id: string | null;
  assigned_user_name: string | null;
  assigned_user_email: string | null;
  /** Raw role code, e.g. `ADMIN` or `SUPER_ADMIN`. */
  assigned_user_role: string | null;
  /** Prefixed with a 2-letter ISO country code, e.g. "PL +48222222222". */
  assigned_user_phone: string | null;
  /** Name of the admin who performed the (re)assignment. */
  assigned_by_user_name: string | null;
  /** Raw role code of the admin who performed the (re)assignment. */
  assigned_by_user_role: string | null;
  /** Email of the admin who performed the (re)assignment. */
  assigned_by_user_email: string | null;
  client_id: string | null;
  created_by: string | null;
  /** ISO-8601 string — format before display */
  created_at: string;
  updated_at: string;
  client_name: string | null;
  /** Sequential client ID, e.g. CLIENT000011 */
  client_user_id: string | null;
  client_email: string | null;
  /** Pre-formatted "countryCode phoneNumber" string assembled by the backend */
  client_phone: string | null;
  client_address_line1: string | null;
  client_address_line2: string | null;
  client_city: string | null;
  client_state: string | null;
  client_zip_code: string | null;
  client_country: string | null;
  /**
   * Enriched status-history timeline, ordered oldest → newest. Only returned by
   * the "my-tasks" detail endpoint (RR-1096); absent on the standard view.
   */
  history?: ContactRequestHistoryEntry[];
  /**
   * Completion "response" (attachments + notes). Reserved for a future backend
   * feature; currently absent. Absent / null / empty all mean "no response".
   */
  response?: ContactRequestResponse;
}

/**
 * Which detail endpoint to read.
 * - `'default'` → `GET /contact-requests/{id}` (SUPER_ADMIN: any; ADMIN: tenant-scoped).
 * - `'my-tasks'` → `GET /contact-requests/my-tasks/{id}` (RR-1096): self-scoped,
 *   returns the task only when assigned to the calling admin (otherwise 404),
 *   and includes the `history` timeline.
 */
export type ViewContactRequestSource = 'default' | 'my-tasks';

export interface ViewContactRequestResult {
  success: boolean;
  data?: ViewContactRequestData;
  error?: string;
  forbidden?: boolean;
}

// ─── API function ─────────────────────────────────────────────────────────────

/**
 * Fetches full detail of a single contact request by its UUID.
 *
 * Endpoint (by `source`):
 * - `'default'` → `GET contact-request/contact-requests/{id}`
 * - `'my-tasks'` → `GET contact-request/contact-requests/my-tasks/{id}` (RR-1096)
 *
 * Access: SUPER_ADMIN (any request) | ADMIN (tenant-scoped, returns 404 if outside tenant).
 * The `'my-tasks'` variant is self-scoped: 404 unless the task is assigned to the caller.
 *
 * @param id - UUID of the contact request (`ContactRequestListItem.id`)
 * @param source - which detail endpoint to read (defaults to `'default'`)
 */
export const viewContactRequest = async (
  id: string,
  source: ViewContactRequestSource = 'default'
): Promise<ViewContactRequestResult> => {
  try {
    const endpoint =
      source === 'my-tasks'
        ? contactRequestEndpoints.VIEW_MY_ASSIGNED_TASK(id)
        : `${contactRequestEndpoints.LIST_CONTACT_REQUESTS}/${id}`;
    const result = await apiService.getAPI(endpoint, {
      skipGlobalErrorRedirect: true,
    });

    if (!result.error) {
      return { success: true, data: result.data as ViewContactRequestData };
    }

    if (result.status === 403) {
      return { success: false, forbidden: true };
    }

    const { errorMessage } = handleContactRequestError(
      result.status as number | undefined,
      result.data,
      !!result.isNetworkError
    );
    return { success: false, error: errorMessage };
  } catch (error) {
    if (error instanceof AxiosError) {
      const { errorMessage } = handleContactRequestError(
        error.response?.status,
        error.response?.data,
        !error.response && !!error.request
      );
      return { success: false, error: errorMessage };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : contactRequestErrorMessages[
              ContactRequestErrorCode.UNEXPECTED_ERROR
            ],
    };
  }
};
